/**
 * Polish task 04 — TanStack Query bindings for the /admin/people surface.
 *
 * User lifecycle goes through mariejois UserManagementService (the
 * authoritative store, browser-reachable via sabaody + the dual-auth
 * interceptor) — NOT the legacy express /api/user/* routes, so role/status/
 * password changes actually affect login + authz (the split-brain fix).
 * Per-user groups + access grants come from enies-lobby AuthzService.
 */ import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authzClient, deliveryClient, userManagementClient } from '../../../lib/connect';
export const USERS_KEY = 'admin:users';
export const USER_GROUPS_KEY = 'admin:user-groups';
export const USER_ASSIGNMENTS_KEY = 'admin:user-assignments';
const PROGRAMS_KEY = 'admin:programs';
/** Paginated user list — the People table data source. */ export function useListUsers(search, page, pageSize = 25, options = {}) {
    const { enabled = true } = options;
    return useQuery({
        queryKey: [
            USERS_KEY,
            search,
            page,
            pageSize
        ],
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await userManagementClient.listUsers({
                search,
                page,
                pageSize
            });
            return {
                users: resp.users,
                total: resp.totalCount ?? 0
            };
        }
    });
}
export function useCreateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>userManagementClient.createUser(args),
        onSuccess: (_r, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    USERS_KEY
                ]
            });
            toast.success(vars.sendInvite ? `Invite sent to ${vars.email}` : 'User created');
        },
        onError: (e)=>{
            // A failed invite after the user row exists still adds them.
            qc.invalidateQueries({
                queryKey: [
                    USERS_KEY
                ]
            });
            toast.error(e instanceof Error ? e.message : 'Failed to create user');
        }
    });
}
/** SAD-953: email the user a fresh set-your-password link. */ export function useResendInvite() {
    return useMutation({
        mutationFn: async (args)=>userManagementClient.resendInvite({
                userId: args.userId
            }),
        onSuccess: (_r, vars)=>toast.success(`Invite sent to ${vars.email}`),
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to resend invite')
    });
}
export function useUpdateUserRole() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>userManagementClient.updateUserRole(args),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    USERS_KEY
                ]
            });
            toast.success('Role updated');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to update role')
    });
}
export function useUpdateUserStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>userManagementClient.updateUserStatus(args),
        onSuccess: (_r, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    USERS_KEY
                ]
            });
            toast.success(vars.active ? 'User activated' : 'User deactivated');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to update status')
    });
}
export function useResetPassword() {
    return useMutation({
        mutationFn: async (args)=>userManagementClient.updateUserPassword(args),
        onSuccess: ()=>toast.success('Password reset'),
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to reset password')
    });
}
export function useDeleteUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>userManagementClient.deleteUser({
                userId: args.userId,
                hard: args.hard ?? false
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    USERS_KEY
                ]
            });
            toast.success('User removed');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to remove user')
    });
}
/** Groups a user belongs to — chips on the row / detail. */ export function useGroupsForUser(userId, enabled = true) {
    return useQuery({
        queryKey: [
            USER_GROUPS_KEY,
            userId
        ],
        enabled: enabled && !!userId,
        queryFn: async ()=>(await authzClient.adminListGroupsForUser({
                userId
            })).groups
    });
}
/** A user's access grants (bundle-on-scope) for the detail panel. */ export function useAssignmentsForUser(userId, enabled = true) {
    return useQuery({
        queryKey: [
            USER_ASSIGNMENTS_KEY,
            userId
        ],
        enabled: enabled && !!userId,
        queryFn: async ()=>(await authzClient.adminListAssignmentsForUser({
                userId,
                includeInactive: false
            })).assignments
    });
}
/**
 * Program typeahead for the jargon-free grant flow. Programs are the objects
 * that own scope nodes today (delivery.programs); the picker lets an admin
 * grant by program NAME instead of pasting a scope_node id.
 */ export function usePrograms(search) {
    return useQuery({
        queryKey: [
            PROGRAMS_KEY,
            search
        ],
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await deliveryClient.listPrograms({});
            const all = resp.programs ?? [];
            const q = search.trim().toLowerCase();
            const rows = q ? all.filter((p)=>(p.programNameFull ?? '').toLowerCase().includes(q) || (p.contractNumber ?? '').toLowerCase().includes(q)) : all;
            return rows.slice(0, 20);
        }
    });
}
/**
 * The jargon-free grant: "who → what program → what level". Ensures the
 * program's scope node exists (AdminCreateScopeNode is idempotent
 * create-or-get) then creates the assignment for the user or group principal.
 * The admin never sees a scope_node id.
 */ export function useGrantOnProgram() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const node = await authzClient.adminCreateScopeNode({
                kind: 'program',
                targetObjectType: 'delivery.programs',
                targetObjectId: args.programId,
                displayName: args.programName,
                sensitivity: 'standard'
            });
            const scopeNodeId = node.scopeNode?.id ?? '';
            if (!scopeNodeId) throw new Error('could not resolve the program scope node');
            return authzClient.adminCreateRoleAssignment({
                userId: args.userId ?? '',
                groupId: args.groupId ?? '',
                roleBundleKey: args.roleBundleKey,
                scopeNodeId,
                assignmentType: args.assignmentType ?? 'primary',
                reason: args.reason,
                effectiveStart: args.effectiveStart,
                effectiveEnd: args.effectiveEnd
            });
        },
        onSuccess: (_r, vars)=>{
            if (vars.userId) qc.invalidateQueries({
                queryKey: [
                    USER_ASSIGNMENTS_KEY,
                    vars.userId
                ]
            });
            toast.success('Access granted');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to grant access')
    });
}
